/**
 * PendingTasksBanner.js
 * M-03 §3.4: Shows pending employee tasks regardless of active context.
 * Displayed transversally across AdminDashboard when there are pending items.
 */
import React from 'react';
import { AlertCircle, ChevronRight, X } from 'lucide-react';
import { useView } from '../contexts/ViewContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';

export const PendingTasksBanner = ({ onDismiss }) => {
  const { language } = useLanguage();
  const { pendingTasks, switchToEmployeeView, isEmployeeView } = useView();

  if (isEmployeeView || pendingTasks.length === 0) return null;

  return (
    <div
      className="bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-200"
      data-testid="pending-tasks-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-orange-100">
              <AlertCircle className="h-4 w-4 text-orange-600" />
            </div>
            <p className="text-sm text-orange-800">
              <span className="font-medium">
                {language === 'es'
                  ? `Tienes ${pendingTasks.length} tarea${pendingTasks.length > 1 ? 's' : ''} pendiente${pendingTasks.length > 1 ? 's' : ''} como empleado`
                  : `You have ${pendingTasks.length} pending employee task${pendingTasks.length > 1 ? 's' : ''}`}
              </span>
              <span className="hidden sm:inline text-orange-600 ml-1">
                — {pendingTasks.map(t => t.title).join(', ')}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-orange-700 hover:text-orange-900 hover:bg-orange-100 text-xs"
              onClick={switchToEmployeeView}
              data-testid="pending-tasks-go"
            >
              {language === 'es' ? 'Ir a mi vista' : 'Go to my view'}
              <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
            {onDismiss && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onDismiss}>
                <X className="h-3.5 w-3.5 text-orange-400" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingTasksBanner;
